import { ComboBox, Link, Space, Text } from '@billy/design-system'
import { ReactElement, useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useClickAway } from 'react-use'

import { isValidEmail } from '../../../../../../../../../../utils'
import { useSendAsEmailForm } from '../../../../../../contexts/sendAsEmailFormContext'
import { useContactPersonItems } from '../../../../../../hooks/useContactPersonItems'
import { useTrackSendAsEmail } from '../../../../../../hooks/useTrackSendAsEmail'
import * as Styled from './styles'

export const CCEmailRecipients = (): ReactElement => {
  const { t } = useTranslation()
  const [shouldAllowAddingCC, setShouldAllowAddingCC] = useState(false)
  const ccRecipientsFieldRef = useRef<HTMLDivElement>(null)
  const isOutsideComboBoxClickRef = useRef(false)
  const { contactPersonItems } = useContactPersonItems()
  const { sendAsEmailForm } = useSendAsEmailForm()
  const { trackSendAsEmail } = useTrackSendAsEmail()
  const { FormItem, watch } = sendAsEmailForm

  const ccEmailRecipients = watch('ccEmailRecipients')

  // Hide "ccEmailRecipients" field after clicking outside ComboBox,
  // onMouseDown && onMouseUp handlers are necessary to assure
  // that it also works correctly for the ComboBox's dropdown.
  useClickAway(ccRecipientsFieldRef, () => {
    if (!isOutsideComboBoxClickRef.current) {
      // Don't hide if there are selected recipients
      if (ccEmailRecipients?.length) {
        return
      }

      setShouldAllowAddingCC(false)
    }
  })

  const handleAddCCButtonClick = useCallback(() => {
    trackSendAsEmail('Add CC clicked')
    setShouldAllowAddingCC(true)
  }, [trackSendAsEmail])

  const handleDropdownMouseDown = () => {
    isOutsideComboBoxClickRef.current = true
  }

  const handleDropdownMouseUp = () => {
    requestAnimationFrame(() => {
      isOutsideComboBoxClickRef.current = false
    })
  }

  if (shouldAllowAddingCC) {
    return (
      <Styled.CCRecipientsFieldWrapper
        onMouseDown={handleDropdownMouseDown}
        onMouseUp={handleDropdownMouseUp}
        ref={ccRecipientsFieldRef}
      >
        <Space size="s6" />
        <FormItem
          name="ccEmailRecipients"
          render={({ field: { onChange, value, ...rest } }) => {
            return (
              <ComboBox
                {...rest}
                addNewItemButtonText={t('external_invoices.send_as_email.modal.form.field.cc_recipients.add_item')}
                focused
                isMultiselect
                items={contactPersonItems}
                maxItems={3}
                onChange={(event) => {
                  onChange(event)
                }}
                placeholder={t('external_invoices.send_as_email.modal.form.field.cc_recipients.placeholder')}
                selectedItemsPrefix={t('external_invoices.send_as_email.modal.form.field.cc_recipients.label')}
                selectedValues={value}
                size="sm"
                validateItem={isValidEmail}
              />
            )
          }}
        />
      </Styled.CCRecipientsFieldWrapper>
    )
  }

  return (
    <Link onClick={handleAddCCButtonClick} underlined>
      <Text weight="bold" variant="bodySM">
        {t('external_invoices.send_as_email.modal.action.add_cc')}
      </Text>
    </Link>
  )
}
